import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthShell from '../v2/auth/AuthShell';
import { pathForScreen } from '../v2/shell/nav';

const Session = createContext({ status: 'connecting', message: '', retry: () => {} });
const PREFIX = 'pipsqueak.portal.';
const HEX = /^[a-f0-9]{64}$/;
function nativeOrigin(origin) {
  try {
    const url = new URL(origin);
    return (['http:', 'https:'].includes(url.protocol) && ['wails.localhost', 'localhost', '127.0.0.1', '[::1]'].includes(url.hostname)) || (url.protocol === 'wails:' && ['wails', 'wails.localhost'].includes(url.hostname));
  } catch { return false; }
}
const random = () => Array.from(crypto.getRandomValues(new Uint8Array(32)), n => n.toString(16).padStart(2, '0')).join('');

// Installed once above all website routes: SPA navigation retains the trusted
// parent relationship, and logout can clear this session from every screen.
export function DesktopPortalSessionProvider({ children }) {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [state, setState] = useState({ status: 'connecting', message: '' });
  const [retry, setRetry] = useState(() => () => {});
  useEffect(() => {
    if (window.parent === window) return undefined;
    let alive = true, parentOrigin = '', verifier = '', challenge = '', sequence = 0, busy = false, hasSession = false;
    let abort, deadline, renew;
    const post = value => { if (parentOrigin) window.parent.postMessage(value, parentOrigin); };
    const fail = message => {
      verifier = ''; busy = false; clearTimeout(deadline);
      setState({ status: 'error', message });
      if (hasSession) { localStorage.removeItem('token'); hasSession = false; navigateRef.current('/desktop-login', { replace: true }); }
    };
    const begin = async () => {
      if (!alive || !parentOrigin || busy) return;
      // Respect a website logout instead of silently signing back in at renewal.
      if (hasSession && !localStorage.getItem('token')) {
        hasSession = false; clearTimeout(renew);
        setState({ status: 'signed-out', message: '' }); return;
      }
      const attempt = ++sequence; busy = true; abort?.abort(); abort = null; clearTimeout(renew);
      setState({ status: 'connecting', message: '' });
      // Never briefly display a different account left in this browser profile.
      try {
        if (!hasSession) localStorage.removeItem('token');
        verifier = random();
        const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
        if (!alive || attempt !== sequence) return;
        challenge = Array.from(new Uint8Array(digest), n => n.toString(16).padStart(2, '0')).join('');
        post({ type: PREFIX + 'ready', challenge });
        deadline = setTimeout(() => { abort?.abort(); sequence++; fail('Connecting your scanner account timed out. Please retry.'); }, 30000);
      } catch { fail('This browser could not prepare a secure sign-in. Please retry.'); }
    };
    setRetry(() => () => { busy = false; begin(); });
    const message = async event => {
      if (event.source !== window.parent || !nativeOrigin(event.origin) || (parentOrigin && event.origin !== parentOrigin) || !event.data || typeof event.data !== 'object') return;
      const data = event.data;
      if (data.type === PREFIX + 'init') {
        parentOrigin = event.origin;
        if (data.reset) {
          sequence++; busy = false; abort?.abort(); abort = null; clearTimeout(deadline); clearTimeout(renew); hasSession = false;
          localStorage.removeItem('token'); navigateRef.current('/desktop-login', { replace: true });
        }
        begin(); return;
      }
      if (!parentOrigin) return;
      if (data.type === PREFIX + 'navigate') {
        if (hasSession && ['profile', 'requests'].includes(data.destination)) navigateRef.current(pathForScreen(data.destination));
        return;
      }
      if (data.type === PREFIX + 'logout') {
        sequence++; busy = false; verifier = ''; abort?.abort(); clearTimeout(deadline); clearTimeout(renew);
        localStorage.removeItem('token');
        post({ type: PREFIX + 'logged-out' });
        setState({ status: 'signed-out', message: '' });
        hasSession = false; navigateRef.current('/login', { replace: true }); return;
      }
      if (!busy || data.challenge !== challenge) return;
      if (data.type === PREFIX + 'error') { fail('Unable to connect your scanner account. Sign in to the scanner and retry.'); return; }
      if (data.type !== PREFIX + 'code' || !HEX.test(data.code) || !verifier || abort) return;
      const attempt = sequence;
      abort = new AbortController();
      try {
        const response = await fetch('/s1/api/auth/portal/redeem', {
          method: 'POST', credentials: 'omit', cache: 'no-store', redirect: 'error', signal: abort.signal,
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code: data.code, verifier })
        });
        const result = await response.json();
        if (!alive || attempt !== sequence) return;
        if (!response.ok || typeof result.token !== 'string' || !result.token || result.token.length > 65536 || result.expires_in < 1 || result.expires_in > 3600) throw Error('Invalid sign-in response');
        verifier = ''; busy = false; clearTimeout(deadline);
        localStorage.setItem('token', result.token);
        setState({ status: 'connected', message: '' });
        post({ type: PREFIX + 'connected', challenge });
        if (!hasSession) navigateRef.current('/dashboard', { replace: true });
        hasSession = true;
        // Refresh this website session through Go while the scanner login is valid.
        renew = setTimeout(begin, Math.max(1000, (result.expires_in > 120 ? result.expires_in - 60 : result.expires_in / 2) * 1000));
      } catch {
        if (alive && attempt === sequence) fail('Your scanner sign-in could not be completed. Please retry.');
      } finally { if (attempt === sequence) abort = null; }
    };
    window.addEventListener('message', message);
    return () => { alive = false; sequence++; abort?.abort(); clearTimeout(deadline); clearTimeout(renew); window.removeEventListener('message', message); };
  }, []);
  return <Session.Provider value={{ ...state, retry }}>{children}</Session.Provider>;
}

export default function DesktopPortalLogin() {
  const { status, message, retry } = useContext(Session);
  return <AuthShell brand={false}><div className="auth-form"><div className="auth-form-inner">
    <h1 className="auth-title">Opening your Portal</h1>
    <p className="auth-sub">{message || 'Connecting your signed-in Pipsqueak account…'}</p>
    {status === 'error' && <button className="auth-btn" onClick={retry}>Try again</button>}
    {window.parent === window && <p>Open Portal from your scanner, or <a href="/login">sign in to the website</a>.</p>}
  </div></div></AuthShell>;
}
